import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import useIsDesktop from "@/hooks/useIsDesktop";
import useVisitorIntent from "@/hooks/useVisitorIntent";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Nav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import Studio from "@/components/landing/Studio";
import Packages from "@/components/landing/Packages";
import Spotlight from "@/components/landing/Spotlight";
import Testimonials from "@/components/landing/Testimonials";
import StatStrip from "@/components/landing/StatStrip";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import UnderConstructionLock from "@/components/landing/UnderConstructionLock";
import ScrollEdgeGlow from "@/components/landing/ScrollEdgeGlow";
import CursorSpotlight from "@/components/landing/CursorSpotlight";
import ChatWidget from "@/components/landing/ChatWidget";
import { ScrollProgressBar } from "@/components/landing/AmbientMotion";
import AdminLogin from "@/pages/AdminLogin";
import AdminHub from "@/pages/AdminHub";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBookings from "@/pages/AdminBookings";
import BookingStatusPage from "@/pages/BookingStatusPage";
import LinksPage from "@/pages/LinksPage";
import ClientPortal from "@/pages/ClientPortal";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminInstall from "@/pages/AdminInstall";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminPipeline from "@/pages/AdminPipeline";
import AdminBoards from "@/pages/AdminBoards";
import AdminBoardDetail from "@/pages/AdminBoardDetail";
import AdminSettings from "@/pages/AdminSettings";
import AdminChatLeads from "@/pages/AdminChatLeads";
import PublicBoardShare from "@/pages/PublicBoardShare";
import AdminWeeklyReports from "@/pages/AdminWeeklyReports";
import LegalPage from "@/pages/LegalPage";
import PayInvoicePage from "@/pages/PayInvoicePage";
import PortalPasswordPage from "@/pages/PortalPasswordPage";
import AdminPayments from "@/pages/AdminPayments";
import AdminInbox from "@/pages/AdminInbox";
import PackageBuyModal from "@/components/payment/PackageBuyModal";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import CookieConsent from "@/components/CookieConsent";

const Landing = () => {
  // Passive intent tracker — watches sections in view, button clicks, video
  // plays, quiz opens. Feeds Sable a `visitor_context` on every chat call
  // so the concierge can personalize openers and follow-ups.
  useVisitorIntent();
  useEffect(() => {
    const handler = (e) => {
      const t = e.target.closest("a[href^='#']");
      if (!t) return;
      const id = t.getAttribute("href");
      if (id && id.length > 1) {
        const el = document.querySelector(id);
        if (el) {
          e.preventDefault();
          window.scrollTo({
            top: el.getBoundingClientRect().top + window.scrollY - 72,
            behavior: "smooth",
          });
        }
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);
  return (
    <div className="app-root grain relative" data-testid="app-root">
      <ScrollProgressBar />
      <ScrollEdgeGlow />
      <CursorSpotlight />
      <div className="relative z-10">
        <Nav />
        <main>
          <Hero />
          <Studio />
          <StatStrip />
          <Testimonials />
          <Packages />
          <Spotlight />
          <Contact />
        </main>
        <Footer />
        <UnderConstructionLock />
      </div>
      <ChatWidget />
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Global "Buy now" listener — package cards dispatch `sb:open-buy` with
 * `{ package_key }` and this modal opens on top of any route.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GlobalBuyModal = () => {
  const [pkg, setPkg] = useState(null);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    // Lazy-load catalog once on first open
    let cancelled = false;
    const handler = async (e) => {
      const key = e?.detail?.package_key;
      if (!key) return;
      let cat = catalog;
      if (!cat) {
        try {
          const { data } = await axios.get(`${API}/payments/config`);
          if (cancelled) return;
          cat = data.packages || {};
          setCatalog(cat);
        } catch {
          return;
        }
      }
      const info = cat[key];
      if (info) setPkg({ key, ...info });
    };
    window.addEventListener("sb:open-buy", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("sb:open-buy", handler);
    };
  }, [catalog]);

  return <PackageBuyModal open={!!pkg} onClose={() => setPkg(null)} packageInfo={pkg} />;
};

function App() {
  const isDesktop = useIsDesktop();
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* On mobile/tablet we disable transforms (reducedMotion="always")
            but framer-motion keeps opacity animations. A short, tween-based
            transition gives sections a subtle one-time fade-in on reveal
            without reintroducing any transform-based jank. */}
        <MotionConfig
          reducedMotion={isDesktop ? "never" : "always"}
          transition={
            isDesktop
              ? undefined
              : { type: "tween", duration: 0.35, ease: "easeOut" }
          }
        >
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/links" element={<LinksPage />} />
          <Route path="/client-portal" element={<ClientPortal />} />
          <Route path="/booking/:id" element={<BookingStatusPage />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/pay/:invoiceId" element={<PayInvoicePage />} />
          <Route path="/portal/reset" element={<PortalPasswordPage mode="reset" />} />
          <Route path="/portal/accept" element={<PortalPasswordPage mode="accept" />} />
          <Route path="/board-share/:token" element={<PublicBoardShare />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute>
                <AdminHub />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/guests"
            element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <ProtectedAdminRoute>
                <AdminBookings />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/marketing"
            element={
              <ProtectedAdminRoute>
                <AdminMarketing />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/install"
            element={
              <ProtectedAdminRoute>
                <AdminInstall />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedAdminRoute>
                <AdminAnalytics />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/pipeline"
            element={
              <ProtectedAdminRoute>
                <AdminPipeline />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/boards"
            element={
              <ProtectedAdminRoute>
                <AdminBoards />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/boards/:slug"
            element={
              <ProtectedAdminRoute>
                <AdminBoardDetail />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedAdminRoute>
                <AdminSettings />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/chat-leads"
            element={
              <ProtectedAdminRoute>
                <AdminChatLeads />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/analytics/reports"
            element={
              <ProtectedAdminRoute>
                <AdminWeeklyReports />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/analytics/reports/:reportId"
            element={
              <ProtectedAdminRoute>
                <AdminWeeklyReports />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedAdminRoute>
                <AdminPayments />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/inbox"
            element={
              <ProtectedAdminRoute>
                <AdminInbox />
              </ProtectedAdminRoute>
            }
          />
        </Routes>
        <CookieConsent />
        <GlobalBuyModal />
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "#11091a",
              border: "1px solid rgba(0,240,255,0.25)",
              color: "#fafafa",
            },
          }}
        />
        </MotionConfig>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
